import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { selectIsShadowModeEnabled } from '../config/config.selector';
import { AgentMode } from './agentMode.constants';

const selectAgentModeState = (state: RootState) => state.agentMode;

/**
 * Read straight off the state rather than through `ai.selector`'s own
 * `selectIsAIAutoMode`, which would import this module's way back into a cycle:
 * `ai.selector` reaches `messagingSlice` for the staff-intervention enum, and
 * that pulls in the middleware, the cart and `orderSlice` — which imports this
 * file. The loop leaves whichever selector initialises second `undefined`, and
 * `createSelector` throws at module load.
 *
 * One field, no logic, so nothing is duplicated but the path to it — the same
 * shape as `selectAgentModeState` above.
 */
const selectIsAIAutoMode = (state: RootState) => state.ai.isAIAutoMode;

const selectAgentMode = createSelector(
  selectAgentModeState,
  (agentMode) => agentMode.mode
);

const selectAgentModeEnteredBy = createSelector(
  selectAgentModeState,
  (agentMode) => agentMode.enteredBy
);

/**
 * HITL-1650 — did the AGENT choose Intercept, or did the console arm it for them?
 *
 * Two places need this exact question and they must not answer it separately:
 *
 *   - the switch, to decide whether `Shadow` is still available. A mode the
 *     agent never chose must stay walkable-back.
 *   - the status band, to decide whether to stop blinking "take over now". The
 *     blink stops because the agent ANSWERED the alert; a console that armed
 *     Intercept on their behalf has not answered anything on their behalf, and
 *     silencing the alert there would hide the request from the one person it
 *     is addressed to.
 *
 * Two copies of that would drift, and the failure mode is quiet: a band that
 * has stopped asking for a takeover nobody has agreed to give.
 *
 * ANDed with the flag, like every predicate in this file, so with supervised
 * mode off nothing can move the mode and this is provably false.
 */
const selectHasAgentChosenIntercept = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  selectAgentModeEnteredBy,
  (isShadowModeEnabled, mode, enteredBy) =>
    isShadowModeEnabled && mode === AgentMode.intercept && enteredBy === 'agent'
);

/**
 * HITL-1650 — may the agent still step back out of `Intercept`?
 *
 * Two ways it becomes theirs to keep, and the switch locks on either:
 *
 *   - they CHOSE it. Their decision stands for the session, exactly as
 *     HITL-1604 wrote it.
 *   - something has ALREADY GONE OUT. `isAIAutoMode` is cleared by every real
 *     interception — a readback, an order, a spoken line — so a false value
 *     while in `Intercept` means the agent has acted on the customer's order
 *     whether or not they ever touched the switch.
 *
 * The second arm is what stops the auto-arm opening a hole. A console-armed
 * `Intercept` is deliberately walkable-back, and without this an agent could
 * take the conversation, speak to the customer and then drop to `Shadow` — the
 * AI stood down, their own surfaces inert, and nobody driving the order.
 *
 * Reading `isAIAutoMode` is not free: four behaviours already key off it and it
 * is noisier than its name suggests. It is the right signal here all the same,
 * and only because this asks the one question it answers honestly — "has
 * anything reached the customer or the POS yet" — read-only, and never written.
 *
 * Distinct from `selectHasAgentChosenIntercept`, which the status band uses.
 * The band asks whether the agent ANSWERED the alert, and only their own press
 * answers it; this asks whether the mode is still theirs to leave. Same state,
 * two questions, and collapsing them would make the band go quiet on an
 * interception the agent never agreed to.
 */
const selectIsAgentModeLocked = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  selectAgentModeEnteredBy,
  selectIsAIAutoMode,
  (isShadowModeEnabled, mode, enteredBy, isAIAutoMode) =>
    isShadowModeEnabled &&
    mode === AgentMode.intercept &&
    (enteredBy === 'agent' || !isAIAutoMode)
);

/**
 * HITL-1606 — the one condition in front of every agent action.
 *
 * True when Supervised AI mode is on AND the agent has not chosen `Intercept`:
 * they may look, but nothing they do may leave the terminal. Read in two kinds
 * of place — the choke points that refuse the action, which are the whole of
 * the safety story, and the controls, which disable themselves so the agent is
 * never invited to attempt something that would be refused.
 *
 * Keyed on the MODE and nothing else. An earlier draft of the mode slice
 * carried a second `engaged` flag, latched on the first interception, and the
 * gate deliberately did not read it: the gate would then have been opened by
 * the very first action it exists to refuse. The flag is gone with HITL-1604's
 * one-way Intercept, but the reasoning is why this predicate still asks only
 * "which mode", and never "how far in".
 *
 * ANDed with `selectIsShadowModeEnabled` — the full `hitl_3_0 && hitl_7_0 &&
 * hitl_shadow_mode` gate — so with the flag off this is always false and every
 * surface behaves exactly as it does today.
 *
 * The slice deliberately does not offer `isArmed` / `isEngaged` / `isSilent`
 * variations: with two modes and a one-way transition there is one question
 * worth asking about whether the agent may act, and this is it. The selector
 * below asks a different question — which control is on screen — and does not
 * gate an action.
 */
const selectIsAgentActionBlocked = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  (isShadowModeEnabled, mode) =>
    isShadowModeEnabled && mode !== AgentMode.intercept
);

/**
 * HITL-1614 — may the agent copy a Live Order line into My Cart?
 *
 * `Shadow` only. In `Intercept` the Live Order lines already ARE the order, so
 * there is nothing to copy into a draft and the control is not rendered.
 */
const selectCanCopyToDraft = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  (isShadowModeEnabled, mode) =>
    isShadowModeEnabled && mode === AgentMode.shadow
);

/**
 * HITL-1615 — may the agent move My Cart into the order?
 *
 * `Intercept` only. It is deliberately NOT the complement of
 * `selectCanCopyToDraft`: the promote CONTROL is offered in `Shadow` too, where
 * pressing it enters `Intercept` first (see `usePromoteDraftCart`). This
 * predicate is the narrower question of whether the write may happen right now,
 * which is what the empty-column collapse keys on.
 */
const selectCanPromoteToOrder = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  (isShadowModeEnabled, mode) =>
    isShadowModeEnabled && mode === AgentMode.intercept
);

export {
  selectAgentModeState,
  selectAgentMode,
  selectAgentModeEnteredBy,
  selectHasAgentChosenIntercept,
  selectIsAgentModeLocked,
  selectIsAgentActionBlocked,
  selectCanCopyToDraft,
  selectCanPromoteToOrder,
};
